import { useState, useEffect } from 'react';

/**
 * Custom hook for managing date range state
 * Provides default date range and persistence to localStorage
 * 
 * @param {string} storageKey - Key for localStorage persistence
 * @param {string} defaultPreset - Default preset to use (default: 'last30days')
 * @returns {Object} { dateRange, setDateRange, resetDateRange }
 */
export const useDateRange = (storageKey = 'dashboard_dateRange', defaultPreset = 'last30days') => {
  const getDefaultDateRange = () => {
    const end = new Date();
    const start = new Date();
    
    switch (defaultPreset) {
      case 'last7days':
        start.setDate(start.getDate() - 7);
        return { startDate: start, endDate: end, label: 'Last 7 Days', preset: 'last7days' };
      case 'last30days':
        start.setDate(start.getDate() - 30);
        return { startDate: start, endDate: end, label: 'Last 30 Days', preset: 'last30days' };
      case 'last90days':
        start.setDate(start.getDate() - 90);
        return { startDate: start, endDate: end, label: 'Last 90 Days', preset: 'last90days' };
      case 'ytd':
        return { 
          startDate: new Date(end.getFullYear(), 0, 1), 
          endDate: end, 
          label: 'Year to Date', 
          preset: 'ytd' 
        };
      case 'alltime':
        // Go back 2 years to capture all historical data
        start.setFullYear(start.getFullYear() - 2);
        return { startDate: start, endDate: end, label: 'All Time', preset: 'alltime' };
      default:
        start.setDate(start.getDate() - 30);
        return { startDate: start, endDate: end, label: 'Last 30 Days', preset: 'last30days' };
    }
  };

  // Initialize from localStorage or use default
  const [dateRange, setDateRangeState] = useState(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        
        // Auto-upgrade old cached date ranges to 'alltime' if they're too restrictive
        // This ensures users see historical data after the update
        const startDate = new Date(parsed.startDate);
        const endDate = new Date(parsed.endDate);
        const daysDiff = Math.floor((endDate - startDate) / (1000 * 60 * 60 * 24));
        
        // If cached range is less than 60 days, use default instead
        if (daysDiff < 60) {
          console.log('📅 Upgrading old date range to show more historical data');
          return getDefaultDateRange();
        }
        
        // Convert string dates back to Date objects
        return {
          ...parsed,
          startDate,
          endDate
        };
      }
    } catch (error) {
      console.error('Error loading date range from localStorage:', error);
    }
    return getDefaultDateRange();
  });

  // Persist to localStorage whenever dateRange changes
  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(dateRange));
    } catch (error) {
      console.error('Error saving date range to localStorage:', error);
    }
  }, [dateRange, storageKey]);

  const setDateRange = (newDateRange) => {
    setDateRangeState(newDateRange);
  };

  const resetDateRange = () => {
    setDateRangeState(getDefaultDateRange());
  };

  // Helper to get date range as query params
  const getQueryParams = () => {
    if (!dateRange || !dateRange.startDate || !dateRange.endDate) {
      return {};
    }

    return {
      startDate: dateRange.startDate.toISOString().split('T')[0],
      endDate: dateRange.endDate.toISOString().split('T')[0]
    };
  };

  return {
    dateRange,
    setDateRange,
    resetDateRange,
    getQueryParams
  };
};

export default useDateRange;

